import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { LowerRole } from '@shared/types';

// ─────────────────────────────────────────────
// 상수
// ─────────────────────────────────────────────

/** 상조 전용 정산 API에서 사용하는 역할 */
export const ROLE = LowerRole.BugoAgencyAdmin;

// (구) REBATE_FEE_RATE = 0.053 상수는 제거 — 수수료율은 상조별 giftAccountConfig.feeRate
// (rebate-table.utils의 DEFAULT_REBATE_FEE_RATE 폴백)를 쓰고, 수수료 자체는 서버 정수값
// 또는 건별 올림(ceilFeeWon)으로만 계산한다. 직접 0.053 곱은 서버와 어긋난다(2026-07 반올림 개정).

/** 정산 완료 내역 캘린더용 1~12월 배열 */
export const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** 정산 관리 탭 */
export type SettlementTab = 'coupon' | 'delivery';

/** 정산 관리 탭 목록 */
export const TABS: { key: SettlementTab; label: string }[] = [
  { key: 'coupon', label: '쿠폰형' },
  { key: 'delivery', label: '배송형' },
];

// ─────────────────────────────────────────────
// 날짜 유틸
// ─────────────────────────────────────────────

/**
 * Date 객체를 `YYYY-MM-DD` 형식의 문자열로 변환한다.
 * date input의 `value` 속성에 직접 사용할 수 있다.
 */
export const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * 한국 시간(KST, Asia/Seoul) 기준으로 현재 연도를 반환한다.
 * 브라우저 시스템 설정에 무관하게 KST 기준으로 계산한다.
 */
export const getKstYear = (): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
  }).formatToParts(new Date());
  return Number(parts.find((p) => p.type === 'year')?.value ?? new Date().getFullYear());
};

/**
 * 한국 시간(KST, Asia/Seoul) 기준으로 오늘의 연·월·일을 반환한다.
 * 기간 필터의 이번달/지난달 빠른 선택 계산에 사용한다.
 */
export const getKstDateParts = (): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const findPart = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: findPart('year'), month: findPart('month'), day: findPart('day') };
};

// ─────────────────────────────────────────────
// 상태 표시 헬퍼
// ─────────────────────────────────────────────

/**
 * `GiftSettlementStatus` 값을 한국어 레이블로 변환한다.
 * @example statusLabel(GiftSettlementStatus.Paid) // '지급완료'
 */
export const statusLabel = (status: GiftSettlementStatus): string =>
  status === GiftSettlementStatus.Paid ? '지급완료' : '정산예정';

/**
 * `GiftSettlementStatus` 값에 대응하는 Tailwind 텍스트/배경 클래스를 반환한다.
 * `className`에 직접 사용한다.
 */
export const statusColor = (status: GiftSettlementStatus): string =>
  status === GiftSettlementStatus.Paid
    ? 'text-green-700 bg-green-50'
    : 'text-amber-700 bg-amber-50';

// ─────────────────────────────────────────────
// 상조(bugoBrand) 조회 헬퍼
// ─────────────────────────────────────────────

// bugoBrand populated 타입 (GiftItemForStore.bugoBrand는 string으로 선언되지만 실제론 populate됨)
export type PopulatedBugoBrand = {
  _id: string;
  bugoAgency?: { corpName?: string; nickName?: string };
  giftAccountConfig?: { viaMari?: boolean };
};

/** 배송이 속한 상조(bugoBrand) id */
export const getBugoBrandId = (d: GiftDeliveryForAdmin): string | undefined => {
  if (typeof d.giftItem !== 'object') return undefined;
  const bb = (d.giftItem as any).bugoBrand;
  if (!bb) return undefined;
  if (typeof bb === 'string') return bb;
  return (bb as PopulatedBugoBrand)._id;
};

/** 상조명 (corpName 우선, 없으면 nickName, 그것도 없으면 id) */
export const getBugoBrandName = (d: GiftDeliveryForAdmin): string => {
  if (typeof d.giftItem !== 'object') return '미확인';
  const bb = (d.giftItem as any).bugoBrand as PopulatedBugoBrand | string | undefined;
  if (!bb) return '미확인';
  if (typeof bb === 'string') return bb;
  return bb.bugoAgency?.corpName || bb.bugoAgency?.nickName || bb._id;
};

/** 마리 경유 여부 (giftAccountConfig.viaMari) */
export const getBugoBrandViaMari = (d: GiftDeliveryForAdmin): boolean | undefined => {
  if (typeof d.giftItem !== 'object') return undefined;
  const bb = (d.giftItem as any).bugoBrand as PopulatedBugoBrand | string | undefined;
  if (!bb || typeof bb === 'string') return undefined;
  return bb.giftAccountConfig?.viaMari;
};
